import { useState, useEffect } from 'react';
import { getRolesList, createRole, updateRole, deleteRole } from '../../../Config/api';
// import { getRolesList, createRole, updateRole, deleteRole } from '../utils/api';

export const useRoleManagement = () => {
    const [roles, setRoles] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);

    const [confirmModal, setConfirmModal] = useState({
        show: false,
        title: '',
        message: '',
        type: 'info',
        confirmText: 'OK',
        cancelText: 'Cancel',
        onConfirm: null,
        onCancel: null
    });

    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

    const fetchRoles = async () => {
        try {
            setLoading(true);
            const data = await getRolesList({
                page: currentPage,
                limit: rowsPerPage,
                search: searchTerm
            });

            if (data.success) {
                const mappedRoles = data.data.map(role => ({
                    id: role.role_id,
                    name: role.role_name,
                    description: role.role_description,
                    status: role.status,
                    userCount: role.user_count,
                    createdAt: role.created_at,
                    updatedAt: role.updated_at,
                    createdByName: role.created_by_name,
                    updatedByName: role.updated_by_name
                }));

                setRoles(mappedRoles);
                setTotalPages(data.pagination.totalPages);
                setTotalRecords(data.pagination.totalRecords);
            }
        } catch (error) {
            setConfirmModal({
                show: true,
                title: 'Error',
                message: error.message || 'Failed to fetch roles',
                type: 'error',
                onConfirm: () => setConfirmModal(prev => ({ ...prev, show: false }))
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateRole = async (formData) => {
        try {
            const data = await createRole({
                role_name: formData.roleName,
                role_description: formData.description,
                created_by: currentUser.userId
            });

            if (data.success) {
                await fetchRoles();
                setConfirmModal({
                    show: true,
                    title: 'Success!',
                    message: 'Role created successfully!',
                    type: 'success',
                    onConfirm: () => setConfirmModal(prev => ({ ...prev, show: false }))
                });
                return { success: true };
            }
        } catch (error) {
            setConfirmModal({
                show: true,
                title: 'Error',
                message: error.message || 'Failed to create role',
                type: 'error',
                onConfirm: () => setConfirmModal(prev => ({ ...prev, show: false }))
            });
            return { success: false };
        }
    };

    const handleUpdateRole = async (roleId, formData) => {
        try {
            const data = await updateRole(roleId, {
                role_name: formData.roleName,
                role_description: formData.description,
                updated_by: currentUser.userId
            });

            if (data.success) {
                await fetchRoles();
                setConfirmModal({
                    show: true,
                    title: 'Success!',
                    message: 'Role updated successfully!',
                    type: 'success',
                    onConfirm: () => setConfirmModal(prev => ({ ...prev, show: false }))
                });
                return { success: true };
            }
        } catch (error) {
            setConfirmModal({
                show: true,
                title: 'Error',
                message: error.message || 'Failed to update role',
                type: 'error',
                onConfirm: () => setConfirmModal(prev => ({ ...prev, show: false }))
            });
            return { success: false };
        }
    };

    const handleDeleteRole = async (role) => {
        try {
            const data = await deleteRole(role.id, currentUser.userId);

            if (data.success) {
                await fetchRoles();
                setConfirmModal({
                    show: true,
                    title: 'Deleted!',
                    message: 'Role deleted successfully!',
                    type: 'success',
                    onConfirm: () => setConfirmModal(prev => ({ ...prev, show: false }))
                });
            }
        } catch (error) {
            setConfirmModal({
                show: true,
                title: 'Error',
                message: error.message || 'Failed to delete role',
                type: 'error',
                onConfirm: () => setConfirmModal(prev => ({ ...prev, show: false }))
            });
        }
    };

    useEffect(() => {
        fetchRoles();
    }, [searchTerm, currentPage, rowsPerPage]);

    return {
        roles,
        searchTerm,
        setSearchTerm,
        currentPage,
        setCurrentPage,
        rowsPerPage,
        setRowsPerPage,
        totalPages,
        totalRecords,
        loading,
        confirmModal,
        setConfirmModal,
        handleCreateRole,
        handleUpdateRole,
        handleDeleteRole
    };
};